import React, { useState } from "react";
import { useFormik } from "formik";
import { useSelector } from "react-redux";
import * as Yup from "yup";
import services from "../../../services/services";
import { toast } from "react-toastify";
import { getallUsersThank } from "../../../store/features/getUsersSlice";
import { useAppDispatch } from "../../../store/app/store";
import { getTourName } from "../../../services/Tour";
import { languages } from "../../../constant/Languages";

const CreateUserModal = () => {
  const dispatch: any = useAppDispatch();
  const { Tours } = useSelector((state: any) => state.tours);
  const [showModal, setShowModal] = useState(false);
  const [tourSearch, setTourSearch] = useState("");
  const [selectedTour, setSelectedTour] = useState<any>(null);

  const filteredTours = (Tours?.data?.data || []).map((tour: any) => ({
    ...tour,
    name: getTourName(tour?.name, "en") || "",
  }));

  const formik = useFormik({
    initialValues: {
      name: "",
      email: "",
      phone: "",
      language: "English",
    },
    validationSchema: Yup.object({
      name: Yup.string().trim(),
      email: Yup.string().email("Invalid email").trim(),
      phone: Yup.string().trim(),
    }).test(
      "either-email-or-phone",
      "Either email or phone should be filled",
      function (values) {
        if (!values.email && !values.phone) {
          return this.createError({
            path: "email",
            message: "Either email or phone is required",
          });
        }
        return true;
      }
    ),
    onSubmit: async (values) => {
      if (!selectedTour) {
        toast.error("Please select a tour");
        return;
      }

      const cartDetails = [
        {
          productId: selectedTour.productId,
          destinationName: selectedTour.name,
          imagesUrl: selectedTour.imageUrl,
          placeId: selectedTour.placeId,
          status: 1,
          amount: selectedTour.sellingPrice,
          tourId: selectedTour.id,
          bundelsId: 1,
        },
      ];

      const payload = {
        ...values,
        email: values.email.trim(),
        phone: values.phone.trim(),
        language: values.language, // sent to backend; drives email language + user.language column
        cartDetails,
        source: "admin",
      };

      try {
        const res = await services.createUserApi(payload);
        if (res.status === true) {
          dispatch(getallUsersThank({ page: 1, pageSize: 15 }));
          toast.success("User Created!");
          handleClose();
        }
      } catch (error: any) {
        toast.error(error?.response?.data?.message || "Error Creating User!");
      }
    },
  });

  const handleClose = () => {
    formik.handleReset(formik.values);
    setSelectedTour(null);
    setTourSearch("");
    setShowModal(false);
  };

  const searchedTours = filteredTours.filter((t: any) =>
    t.name?.toLowerCase().includes(tourSearch.toLowerCase())
  );

  return (
    <>
      <button
        className="rounded-3xl bg-dimblack flex justify-center items-center w-36 h-10 mt-6"
        type="button"
        onClick={() => setShowModal(true)}
      >
        <svg className="w-3.5 h-3.5 mr-2 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
        <span className="text-white text-sm">Create User</span>
      </button>

      {showModal && (
        <>
          <div className="fixed inset-0 flex items-center justify-center z-50">
            <div className="bg-white w-[550px] max-h-[85vh] rounded-xl shadow-2xl flex flex-col overflow-hidden">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                <h3 className="text-lg font-bold text-gray-800">Create User</h3>
                <button
                  onClick={handleClose}
                  className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition"
                >
                  <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Body */}
              <form onSubmit={formik.handleSubmit} className="flex flex-col flex-1 overflow-hidden">
                <div className="px-6 py-5 space-y-4 overflow-y-auto flex-1">
                  {/* Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                    <input
                      name="name"
                      type="text"
                      placeholder="Enter user name"
                      value={formik.values.name}
                      onChange={formik.handleChange}
                      className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm outline-none focus:border-aqua transition"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input
                      name="email"
                      type="text"
                      placeholder="Email address"
                      value={formik.values.email}
                      onChange={formik.handleChange}
                      className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm outline-none focus:border-aqua transition"
                    />
                    {formik.errors.email && formik.touched.email && (
                      <p className="text-red-500 text-xs mt-1">{formik.errors.email}</p>
                    )}
                  </div>

                  {/* Phone */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                    <input
                      name="phone"
                      type="text"
                      placeholder="Phone number"
                      value={formik.values.phone}
                      onChange={formik.handleChange}
                      className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm outline-none focus:border-aqua transition"
                    />
                  </div>

                  {/* Preferred email language */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email Language
                    </label>
                    <select
                      name="language"
                      value={formik.values.language}
                      onChange={formik.handleChange}
                      className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm outline-none focus:border-aqua transition bg-white"
                    >
                      {languages.map((lang) => (
                        <option key={lang.code} value={lang.name}>
                          {lang.name}
                        </option>
                      ))}
                    </select>
                    <p className="text-[11px] text-gray-500 mt-1">
                      Transactional emails (welcome, tour-added) will be sent in this language.
                    </p>
                  </div>

                  {/* Tour Selection — Searchable */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Select Tour <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="Search tours..."
                      value={tourSearch}
                      onChange={(e) => setTourSearch(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm outline-none focus:border-aqua transition mb-2"
                    />
                    <div className="border rounded-lg max-h-[200px] overflow-y-auto">
                      {searchedTours.length > 0 ? (
                        searchedTours.map((tour: any) => (
                          <div
                            key={tour.productId}
                            onClick={() => setSelectedTour(tour)}
                            className={`px-4 py-2.5 cursor-pointer border-b border-gray-50 text-sm transition ${
                              selectedTour?.productId === tour.productId
                                ? "bg-aqua bg-opacity-10 text-aqua font-semibold"
                                : "hover:bg-gray-50"
                            }`}
                          >
                            {tour.name || tour.title || tour.productId}
                          </div>
                        ))
                      ) : (
                        <div className="px-4 py-4 text-center text-gray-400 text-sm">
                          No tours found
                        </div>
                      )}
                    </div>
                    {selectedTour && (
                      <div className="mt-2 p-2.5 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">
                        Selected: <strong>{selectedTour.name}</strong>
                      </div>
                    )}
                  </div>
                </div>

                {/* Footer */}
                <div className="flex gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="px-5 py-2.5 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-100 transition"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={!selectedTour}
                    className={`px-5 py-2.5 rounded-lg text-sm font-medium text-white transition ${
                      selectedTour
                        ? "bg-aqua hover:opacity-90"
                        : "bg-gray-300 cursor-not-allowed"
                    }`}
                  >
                    Create User
                  </button>
                </div>
              </form>
            </div>
          </div>
          <div className="fixed inset-0 bg-black bg-opacity-25 z-40" onClick={handleClose} />
        </>
      )}
    </>
  );
};

export default CreateUserModal;
